<script setup lang="ts">
import { computed } from 'vue';
import ColumnMapping from '@/components/ColumnMapping.vue';
import DateTimeValue from '@/components/record/fields/DateTimeValue.vue';
import NumberValue from '@/components/record/fields/NumberValue.vue';
import PasswordValue from '@/components/record/fields/PasswordValue.vue';
import PersonValue from '@/components/record/fields/PersonValue.vue';
import RelatedRecordValue from '@/components/record/fields/RelatedRecordValue.vue';
import StatusValue from '@/components/record/fields/StatusValue.vue';
import TextValue from '@/components/record/fields/TextValue.vue';
import YesNoValue from '@/components/record/fields/YesNoValue.vue';
import { fieldFamilyOf, fieldKindFor, fieldValueOf, propertyLabel } from '@/components/record/fields/fieldKind';
import type { TableColumnStaticProps } from '@/types/tableTypes';

/**
 * One field's value in a record's Details, drawn for its kind. It picks the
 * component of the kind's family, so kinds in one family share a component.
 * Tables do not use this: they keep ColumnMapping.
 */
const props = defineProps<{
    entityId: string;
    column: TableColumnStaticProps;
    row: Record<string, unknown>;
}>();

const kind = computed(() => fieldKindFor(props.entityId, props.column));
const family = computed(() => fieldFamilyOf(kind.value));
const value = computed(() => fieldValueOf(props.row, props.column));
const label = computed(() => propertyLabel(props.entityId, props.column, kind.value));
/** The record's id. A password shown on one record hides again on the next. */
const recordKey = computed(() => props.row.id as string | number | undefined);
// A list of related records uses the table's own cell: a count, a few names,
// and the count opens the whole list with a search box. The cell reads a list
// only from a `Model[]` type, and some lists are typed as one model, such as
// Account.tags as `Tag`, so the type is made a list here.
const listDataType = computed(() => (props.column.dataType.endsWith('[]') ? props.column.dataType : `${props.column.dataType}[]`));
// The cell reads a list only from a field such as `locations.display_string`.
// models.json maps most lists to that, but not every one: a Unit's
// `location_zone_players` has no mapping, and drew as "[object Object]".
const listField = computed(() => (props.column.key.includes('.') ? props.column.key : `${props.column.key}.display_string`));
</script>

<template>
  <PasswordValue v-if="kind === 'password'" :value="value" :label="label" :record-key="recordKey" />
  <ColumnMapping v-else-if="kind === 'relatedList'" :field="listField" :data-type="listDataType" :data="row" />
  <RelatedRecordValue v-else-if="family === 'relatedRecord'" :column="column" :value="value" />
  <PersonValue v-else-if="family === 'person'" :value="value" />
  <StatusValue v-else-if="family === 'status'" :entity-id="entityId" :column="column" :value="value" />
  <YesNoValue v-else-if="family === 'yesNo'" :value="value" />
  <DateTimeValue v-else-if="family === 'dateTime'" :value="value" />
  <NumberValue v-else-if="family === 'number'" :kind="kind" :entity-id="entityId" :column="column" :row="row" :value="value" />
  <TextValue v-else :kind="kind" :label="label" :value="value" />
</template>
